import { useEffect, useState } from "react";
import { AlertTriangle, Check, ChevronDown, Loader2, Sparkles } from "lucide-react";

const fmtS = (s) => (s < 60 ? `${Math.round(s)} s` : `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`);

export const AIProgress = ({ job, title, compact, testid = "ai-progress" }) => {
  const run = !!job && ["queued", "running"].includes(job.status);
  const [t, setT] = useState(Date.now());
  const [open, setOpen] = useState(!compact);
  useEffect(() => {
    if (!run) return undefined;
    const i = setInterval(() => setT(Date.now()), 1000);
    return () => clearInterval(i);
  }, [run]);
  if (!job) return null;
  const p = job.progress || 0;
  const start = Date.parse(job.created_at || "") || t;
  const end = run ? t : Date.parse(job.updated_at || "") || t;
  const el = Math.max(0, (end - start) / 1000);
  const eta = run && p > 4 ? (el * (100 - p)) / p : null;
  const steps = (job.steps || []).filter((s) => s.msg && s.msg !== "Iniciado");
  const err = job.status === "error";
  return (
    <div className={`rounded-2xl border ${err ? "border-rose-200 bg-rose-50/50" : "border-[#E2DDD5] bg-white"} ${compact ? "p-3" : "p-5"}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${run ? "ai-pulse bg-[#FDF0EB] text-[#E8643C]" : err ? "bg-rose-100 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
          {run ? <Sparkles size={14} /> : err ? <AlertTriangle size={14} /> : <Check size={14} />}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{title || job.filename}</p>
          <p className="truncate text-xs text-[#8C867A]" data-testid={`${testid}-msg`}>{err ? job.message || "No se pudo procesar" : run ? job.message || "En cola…" : `Listo en ${fmtS(el)} · ${steps.length} pasos completados`}</p>
        </div>
        <span className="text-sm font-bold tabular-nums" data-testid={`${testid}-pct`}>{p}%</span>
        {steps.length > 0 && <button onClick={() => setOpen(!open)} className="rounded-lg p-1 text-[#8C867A] hover:bg-[#F2F0EC]" data-testid={`${testid}-toggle`} title="Ver detalle">
          <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} /></button>}
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#F2F0EC]">
        <div className={`h-full rounded-full transition-[width] duration-700 ${err ? "bg-rose-500" : run ? "ai-shimmer" : "bg-emerald-500"}`} style={{ width: `${Math.max(p, run ? 5 : 0)}%` }} />
      </div>
      {run && <p className="mt-1 text-[11px] text-[#8C867A]">Risk AI Engine · {eta != null ? `≈ ${fmtS(eta)} restantes` : "estimando tiempo…"}</p>}
      {open && steps.length > 0 && (
        <ol className="mt-3 space-y-1.5 border-l border-[#E2DDD5] pl-3" data-testid={`${testid}-log`}>
          {steps.slice(-8).map((s, i, a) => {
            const last = i === a.length - 1 && run;
            return (
              <li key={`${i}-${s.msg}`} className="flex items-start gap-2 text-xs" style={{ animation: "rise .25s ease-out both" }}>
                {last ? <Loader2 size={12} className="mt-0.5 shrink-0 animate-spin text-[#E8643C]" /> : <Check size={12} className="mt-0.5 shrink-0 text-emerald-600" />}
                <span className={last ? "font-medium text-[#111]" : "text-[#5C5850]"}>{s.msg}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export const Skeleton = ({ className = "h-4 w-full" }) => <div className={`skeleton rounded-lg ${className}`} />;

export const EmptyState = ({ icon: I, title, text, children, testid }) => (
  <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[#E2DDD5] bg-[#FAF8F5] px-6 py-12 text-center" data-testid={testid}>
    <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white text-[#E8643C] shadow-sm"><I size={24} /></span>
    <p className="font-semibold">{title}</p>
    {text && <p className="max-w-sm text-sm text-[#8C867A]">{text}</p>}
    {children}
  </div>
);
